import React, { useRef, useState } from 'react';
import { CDN2 } from '@/helpers/api';
import { basicPageProps, fetchTemplate } from '@/utils/isServer';
import { Breadcrumb, Form, Input, Button, Select, Radio, message } from 'antd';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import CreatorsTable from '@/components/CreatorsTable';
import ReCAPTCHA from "react-google-recaptcha";
import { modalType } from '@/components/Modal';
const { TextArea } = Input;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const {configInfo, airports } = await basicPageProps(ctx);

  return {
    props: {
      airports,
      recaptchaSiteKey: configInfo.recaptchaSiteKey,
    },
  };
};

function Creators({recaptchaSiteKey}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const captchaRef = useRef(null);

  const handleSubmit = async (values: any) => {
    let recaptchaToken = '';
    if (captchaRef.current) {
      try {
        recaptchaToken = await captchaRef.current.executeAsync();
      }
      catch (e) {
          modalType({
            type: 'error',
            title: 'Error',
            content: (
              <div className="text-left">
                <p>Error</p>
                <p>
                  Captcha error
                </p>
              </div>
            ),
          });
        captchaRef.current.reset();
        return;
      }
      captchaRef.current.reset();
    }
    setLoading(true);
    const params = {
      type: 2,
      fullName: form.getFieldValue('fullName'),
      email: form.getFieldValue('email'),
      socialLinks: form.getFieldValue('socialLinks'),
      followerCount: form.getFieldValue('followerCount'),
      audienceDemographic: form.getFieldValue('audienceDemographic'),
      whyPartner: form.getFieldValue('whyPartner'),
      interest: form.getFieldValue('interest'),
      recaptchaToken
    };

    fetch('/api/Subscriber/RegisterGeneric', {
      ...fetchTemplate,
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((success) => {
        setLoading(false);
        message.success('Application submitted succesfully!');
        form.resetFields();
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  return (
    <div>
      <Head>
        <title>Creators Partner | On Air Parking</title>
        <meta
          name="description"
          content="We believe the only way to create partnerships is through win-win relatitonships. Partner with On Air Parking."
          key="description"
        />
        <meta
          property="og:url"
          content="https://www.onairparking.com/partner"
          key="og:url"
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta
          property="og:title"
          content="Creators Partner | On Air Parking"
          key="og:title"
        />
        <meta
          property="og:description"
          content="We believe the only way to create partnerships is through win-win relatitonships. Partner with On Air Parking."
          key="og:description"
        />
        <meta
          property="og:image"
          content={CDN2() + '/staticmyapp/oglogo-white.png'}
          key="og:image"
        />
      </Head>
      <div className="container mx-auto w-full md:max-w-6xl p-4">
        <Breadcrumb className="text-sm md:text-base mt-4 mb-8">
          <Breadcrumb.Item>
            <a href="/">Home</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Creators Partner</Breadcrumb.Item>
        </Breadcrumb>
        <h2 className="text-xl lg:text-3xl font-bold">
          Become an Official On Air Parking Creator Partner
        </h2>
        <p className="text-lg">
          We’re looking for creators who love to travel and saving money. Whether
          you're a TikTok influencer, YouTube reviewer, or Instagram
          storyteller, or someone with an awesome blog, we offer a fun and flexible way to partner with us and
          earn.
        </p>
        <div className="mb-6">
          <h3 className="text-lg font-semibold mt-4">Perks</h3>
          <ul className="list-disc list-inside text-base ml-4 mt-2">
            <li>Paid UGC opportunities</li>
            <li>Free/discounted parking</li>
            <li>Affiliate earning potential</li>
            <li>Expanded reach through reposts, stories, and email blasts</li>
            <li>
              Opportunity to be featured in our guest blog and email promotions
            </li>
          </ul>

          <h3 className="text-lg font-semibold mt-6">Earnings Breakdown</h3>
          <p className="text-base mt-2">
            Earn <strong>$5 per reservation</strong> +{' '}
            <strong>5% of net sales</strong> (daily parking rate) for every
            first-time customer booking using your promo code or custom link.
          </p>
          <p className="text-sm text-gray-600 mt-1">
            <strong>Note:</strong> Discounts are for one-time use per customer.
            Commissions apply to first-time purchases only.
          </p>
          <p className="text-base mt-4 font-medium">
            Earnings Estimator based on average ticket value below
          </p>
          <CreatorsTable />
        </div>
        <div className="mb-8">
          <h3 className="text-lg font-semibold mt-8 mb-2">How It Works</h3>
          <ol className="list-decimal list-inside text-base ml-4">
            <li>Apply below</li>
            <li>We review your profile and content</li>
            <li>
              If there’s a fit, we’ll reach out with your partnership agreement
            </li>
          </ol>
          <h3 className="text-xl font-semibold my-4">Creator Application</h3>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item
                  hasFeedback
                  name="fullName"
                  label="Full Name"
                  aria-label="Full Name"
                  rules={[
                    { required: true, message: 'Please enter your name' },
                  ]}
                >
                  <Input placeholder="John Doe" />
                </Form.Item>

                <Form.Item
                  hasFeedback
                  name="email"
                  label="Email"
                  aria-label="Email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Enter a valid email' },
                  ]}
                >
                  <Input placeholder="you@example.com" />
                </Form.Item>
              </div>

              <Form.Item
                hasFeedback
                name="socialLinks"
                label="Social Media Links (IG, TikTok, YouTube, etc.)"
                aria-label="socialLinks"
                rules={[{ required: true, message: 'Please enter your links' }]}
              >
                <TextArea
                  rows={3}
                  placeholder="https://instagram.com/yourprofile, https://tiktok.com/@yourprofile"
                />
              </Form.Item>

              <Form.Item
                hasFeedback
                name="followerCount"
                label="Follower Count (per platform)"
                aria-label="followerCount"
                rules={[
                  { required: true, message: 'Please provide follower counts' },
                ]}
              >
                <TextArea
                  rows={2}
                  placeholder="IG: 5,000 | TikTok: 8,000 | YouTube: 1,200"
                />
              </Form.Item>

              <Form.Item
                hasFeedback
                name="audienceDemographic"
                label="Audience Demographic (brief)"
                aria-label="audienceDemographic"
                rules={[
                  { required: true, message: 'Please describe your audience' },
                ]}
              >
                <TextArea
                  rows={2}
                  placeholder="Mainly US-based travelers, ages 25–34"
                />
              </Form.Item>

              <Form.Item
                hasFeedback
                name="whyPartner"
                aria-label="whyPartner"
                label="Why do you want to partner with On Air Parking?"
                rules={[{ required: true, message: 'Tell us why!' }]}
              >
                <TextArea rows={3} />
              </Form.Item>

              <Form.Item
                hasFeedback
                name="interest"
                label="Are you interested in:"
                aria-label="interest"
                rules={[{ required: true, message: 'Please select an option' }]}
              >
                <Radio.Group>
                  <Radio value="UGC">UGC</Radio>
                  <Radio value="Affiliate">Affiliate</Radio>
                  <Radio value="Both">Both</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item>
                <div className="justify-items-center">
                  {!!recaptchaSiteKey && (
                    <ReCAPTCHA
                      sitekey={recaptchaSiteKey}
                      ref={captchaRef}
                      size="invisible"
                    />
                  )}
                </div>
                <Button
                  type="primary"
                  loading={loading}
                  htmlType="submit"
                  className="bg-primary text-white"
                >
                  Submit Application
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Creators;